import { CardActions, useMediaQuery, useTheme } from '@material-ui/core';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { CheckboxWithLabel } from 'formik-material-ui';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  checkSsoAzureConfig,
  checkSsoJwtConfig,
  checkSsoSamlConfig,
} from 'actions/app.actions';
import WindowsLogoUrl from 'assets/windows.png';
import { BasicTextWithUIGuidelines } from 'component-library/atoms/BasicTextWithUIGuidelines';
import { LabelWithUIGuidelines } from 'component-library/atoms/LabelWithUIGuidelines';
import { LinkWithUIGuidelines } from 'component-library/atoms/LinkWithUIGuidelines';
import { TitleWithUIGuidelines } from 'component-library/atoms/TitleWithUIGuidelines';
import { PrimaryButton } from 'components/Button';
import InputField from 'components/InputField';
import { ROUTES } from 'constants/index';
import { ICustomTheme } from 'interfaces';
import {
  appConfigSelector,
  ssoAzureSelector,
  ssoJwtSelector,
  ssoSamlSelector,
} from 'reducers/app.reducer';
import * as S from './LoginForm.styles';
import useLoginFormEvents, {
  useRedirectWhenAuthenticated,
} from './use-login-form.hooks';

function SSOLogin({
  action,
  contentText,
}: {
  action?: JSX.Element;
  contentText: string;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <S.SSOCard>
      <S.SSOCardContent>
        <TitleWithUIGuidelines title={t('auth.login.sso.title')} />
        <BasicTextWithUIGuidelines text={contentText} />
      </S.SSOCardContent>
      <CardActions>{action}</CardActions>
    </S.SSOCard>
  );
}

function Separator(): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  return (
    <S.SeparatorContainer role="separator">
      <S.SeparatorSlide />
      <S.SeparatorContent isMobile={isMobile}>
        {t('auth.login.separator')}
      </S.SeparatorContent>
      <S.SeparatorSlide />
    </S.SeparatorContainer>
  );
}

function DefaultLogin({
  isShrunken,
  isSubscriptionEnabled,
}: {
  isShrunken: boolean;
  isSubscriptionEnabled: boolean;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();
  const { handleLogin, initialValues, loading, loginSchema, t } =
    useLoginFormEvents();

  return (
    <>
      <TitleWithUIGuidelines title={t('auth.login.title')} />
      <Formik
        initialValues={initialValues}
        validationSchema={loginSchema}
        onSubmit={handleLogin}
      >
        <Form>
          <S.InputsContainer>
            <S.InputContainer isShrunken={isShrunken}>
              <LabelWithUIGuidelines label={t('auth.global.username')} />
              <InputField
                type="text"
                name="username"
                label={t('auth.global.username')}
                variant="outlined"
              />
              <ErrorMessage name="username" component="div" />
            </S.InputContainer>
            <S.InputContainer isShrunken={isShrunken}>
              <LabelWithUIGuidelines label={t('auth.global.password')} />
              <InputField
                type="password"
                name="password"
                label={t('auth.global.password')}
                variant="outlined"
              />
              <ErrorMessage name="password" component="div" />
            </S.InputContainer>
          </S.InputsContainer>
          <S.CheckboxAndPasswordForgot>
            <S.StyledFormControlLabel
              control={
                <Field
                  component={CheckboxWithLabel}
                  name="rememberMe"
                  id="rememberMe"
                />
              }
              label={
                <LabelWithUIGuidelines label={t('auth.login.remember_me')} />
              }
            />
            <LinkWithUIGuidelines
              url={ROUTES.FORGOT_PASSWORD}
              textLink={t('auth.login.password_forgotten')}
              isInternalLink={true}
              color={theme.colors.paletteFromBO.main}
              fontWeight="bold"
              withUnderline
            />
          </S.CheckboxAndPasswordForgot>
          <div css={{ textAlign: 'center' }}>
            <S.ButtonContainer isShrunken={isShrunken}>
              <PrimaryButton
                label={t('auth.global.login')}
                isDisabled={loading}
                isLoading={loading}
              />
            </S.ButtonContainer>
            {!isShrunken && isSubscriptionEnabled && (
              <S.Subscribe>
                <BasicTextWithUIGuidelines text={t('auth.login.no_account')} />
                <LinkWithUIGuidelines
                  isInternalLink
                  url={ROUTES.SUBSCRIBE}
                  textLink={t('auth.signup.title')}
                  withUnderline
                  color={theme.colors.paletteFromBO.main}
                  fontWeight="bold"
                />
              </S.Subscribe>
            )}
          </div>
        </Form>
      </Formik>
    </>
  );
}

export default function LoginForm(): JSX.Element {
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const { t } = useTranslation();
  const { programUrl, postUserRegister, companyName, companyUrlDomain } =
    useSelector(appConfigSelector);
  const isSubscriptionEnabled = !!postUserRegister;
  const hasSsoAzureConfigured = useSelector(ssoAzureSelector);
  const hasSsoJwtConfigured = useSelector(ssoJwtSelector);
  const hasSsoSamlConfigured = useSelector(ssoSamlSelector);
  const dispatch = useDispatch();

  const hasSSO =
    hasSsoAzureConfigured || hasSsoJwtConfigured || hasSsoSamlConfigured;

  useRedirectWhenAuthenticated();

  useEffect(() => {
    dispatch(checkSsoAzureConfig.request());
    dispatch(checkSsoJwtConfig.request());
    dispatch(checkSsoSamlConfig.request());
  }, [dispatch]);

  return (
    <>
      {hasSsoAzureConfigured && (
        <SSOLogin
          contentText={t('auth.login.sso.azureContentTextSsoCard')}
          action={
            <S.SSOButton
              variant="contained"
              href={`${programUrl}${ROUTES.SSO_AZURE_CONNECT}`}
              startIcon={
                WindowsLogoUrl ? (
                  <img src={WindowsLogoUrl} alt="Windows Logo" />
                ) : undefined
              }
              isMobile={isMobile}
            >
              {t('auth.login.sso.azureButtonText')}
            </S.SSOButton>
          }
        />
      )}
      {hasSsoJwtConfigured && companyName && companyUrlDomain && (
        <SSOLogin
          contentText={t('auth.login.sso.contentTextSsoCard', {
            companyName: companyName,
          })}
          action={
            <PrimaryButton href={companyUrlDomain}>
              {t('auth.login.sso.ssoButtonText', { companyName: companyName })}
            </PrimaryButton>
          }
        />
      )}
      {hasSsoSamlConfigured && companyName && companyUrlDomain && (
        <SSOLogin
          contentText={t('auth.login.sso.contentTextSsoCard', {
            companyName: companyName,
          })}
          action={
            <PrimaryButton href={companyUrlDomain}>
              {t('auth.login.sso.ssoButtonText', { companyName: companyName })}
            </PrimaryButton>
          }
        />
      )}
      <S.BottomPartWrapper isShrunken={hasSSO}>
        {hasSSO && <Separator />}
        <DefaultLogin
          isShrunken={hasSSO}
          isSubscriptionEnabled={isSubscriptionEnabled}
        />
      </S.BottomPartWrapper>
    </>
  );
}
